import {DataType, IsMaskedDataRow} from 'data-frames/DataFrameTypes';
import {RegressionAnalysis} from 'ui-library/charting/statistics/regression/RegressionTypes';
import {SuccessfulAnalysis} from 'ui-library/charting/statistics/Types';

import {BenchlingChartProperties} from './internals/BenchlingChartContext';

export interface AxisTypes {
  errorValue?: DataType.NUMBER;
  isMasked?: DataType.BOOLEAN;
  isOutlier?: DataType.BOOLEAN;
  x: AxisCompatibleDataType;
  y: DataType.NUMBER;
}

/**
 * @deprecated Only used for ScatterPlot
 */
export interface IsOutlierDataRow {
  isOutlier?: unknown;
}

/**
 * @deprecated Only used for ScatterPlot
 */
export interface PointWarningDataRow {
  pointWarning?: unknown;
}

/**
 * @deprecated Only used for ScatterPlot
 */
export interface CartesianPoint<XType = unknown, YType = unknown>
  extends IsMaskedDataRow, IsOutlierDataRow, PointWarningDataRow {
  x: XType;
  y: YType;
  errorValue?: YType | null;
}

export type NumericCartesianPoint = CartesianPoint<number, number>;

export type LabelCompatibleType = number | string;

interface BaseDataset<DataPoint extends CartesianPoint> {
  points: Array<DataPoint>;
}

export type UnlabeledDataset<DataPoint extends CartesianPoint = CartesianPoint> = BaseDataset<DataPoint>;
export type LabeledDataset<DataPoint extends CartesianPoint = CartesianPoint> = BaseDataset<DataPoint> & {
  label: unknown;
};
export type Dataset<DataPoint extends CartesianPoint = CartesianPoint> =
  | UnlabeledDataset<DataPoint>
  | LabeledDataset<DataPoint>;
export type Datasets<DataPoint extends CartesianPoint = CartesianPoint> =
  | [UnlabeledDataset<DataPoint>]
  | Array<LabeledDataset<DataPoint>>;
export type DatasetWithRegression<DataPoint extends CartesianPoint = CartesianPoint> = Dataset<DataPoint> & {
  regressionAnalysis: SuccessfulAnalysis<RegressionAnalysis>;
};

export function isLabeledDataset(dataset: Dataset): dataset is LabeledDataset {
  // eslint-disable-next-line benchlint/no-in-operator
  return 'label' in dataset;
}

export function areLabeledDatasets(datasets: Datasets): datasets is Array<LabeledDataset> {
  return datasets.every((dataset) => isLabeledDataset(dataset));
}

export function datasetHasRegression(dataset: Dataset): dataset is DatasetWithRegression {
  // eslint-disable-next-line benchlint/no-in-operator
  return 'regressionAnalysis' in dataset;
}

export enum AxisScale {
  LINEAR = 'LINEAR',
  LOG = 'LOG',
}

export type AxisCompatibleDataType = DataType.DATE_OR_TIMESTAMP | DataType.NUMBER | DataType.STRING;

export const AXIS_COMPATIBLE_DATA_TYPES: Array<AxisCompatibleDataType> = [
  DataType.DATE_OR_TIMESTAMP,
  DataType.NUMBER,
  DataType.STRING,
];

export const DATA_TYPE_TO_SUPPORTED_AXIS_SCALE: {[k in AxisCompatibleDataType]: Array<AxisScale>} = {
  [DataType.DATE_OR_TIMESTAMP]: [AxisScale.LINEAR],
  [DataType.NUMBER]: [AxisScale.LINEAR, AxisScale.LOG],
  [DataType.STRING]: [AxisScale.LINEAR],
};

export interface BaseAxisConfig {
  hideLabel?: boolean;
  label: string;
}

export interface DiscreteAxisConfig extends BaseAxisConfig {}

export interface ContinuousAxisConfig extends BaseAxisConfig {
  // if zero tick appears. no-ops if axis must include zero, e.g. data has positive and negative values
  includeZero: boolean;
  min?: number | null;
  max?: number | null;
  scale: AxisScale;
}

export type AxisConfig = ContinuousAxisConfig | DiscreteAxisConfig;

export function isContinuousAxisConfig(axisConfig: AxisConfig): axisConfig is ContinuousAxisConfig {
  return axisConfig.hasOwnProperty('includeZero') && axisConfig.hasOwnProperty('scale');
}

export interface ErrorBarConfig {
  errorValueLabel: string;
}

export interface LegendConfig {
  label: string;
}

export type BenchlingChartPropWrapper<TChartProps extends object> = TChartProps & BenchlingChartProperties;
